import { Injectable } from '@angular/core';
import { EmailMessage } from '../email-models/email-message.interface';
import { EmailAttachment } from '../email-models/email-attachment.interface';

export type EmailImageDeliveryMode = 'server-hosted' | 'attachment' | 'base64';

@Injectable({
  providedIn: 'root',
})
export class EmailCompilationService {
  async buildEmailMessage(
    emailMessage: EmailMessage,
    contentId: string,
    bannerPath: string,
    imageDeliveryMode: EmailImageDeliveryMode,
  ): Promise<EmailMessage> {
    switch (imageDeliveryMode) {
      case 'server-hosted':
        return this.buildHtmlEmailMessage(emailMessage, bannerPath);
      case 'attachment': {
        const inlineImageAttachment = await this.buildInlineImageAttachment(
          contentId,
          bannerPath,
        );

        return this.buildHtmlEmailMessage(
          emailMessage,
          `cid:${contentId}`,
          [inlineImageAttachment],
        );
      }
      case 'base64':
        return this.buildHtmlEmailMessage(
          emailMessage,
          await this.encodeImageAsDataUrl(bannerPath),
        );
      default: {
        const unsupportedMode: never = imageDeliveryMode;
        throw new Error(`Unsupported image delivery mode: ${unsupportedMode}`);
      }
    }
  }

  private buildHtmlEmailMessage(
    initialEmailMessage: EmailMessage,
    imageSource: string,
    attachments?: EmailAttachment[],
  ): EmailMessage {
    const emailMessage: EmailMessage = {
      ...initialEmailMessage,
      bodyType: 'HTML',
      body: this.buildEmailBody(initialEmailMessage, imageSource),
      hasAttachments: Boolean(attachments?.length),
    };

    if (attachments?.length) {
      emailMessage.attachments = attachments;
    }

    return emailMessage;
  }

  private async buildInlineImageAttachment(
    birthdayBannerContentId: string,
    imagePath: string,
  ): Promise<EmailAttachment> {
    const imageBlob = await this.getImageBlob(imagePath);
    const fileName = this.getFileNameFromPath(imagePath);

    return {
      '@odata.type': '#microsoft.graph.fileAttachment',
      name: fileName,
      contentType: imageBlob.type || this.getContentTypeFromFileName(fileName),
      contentBytes: await this.blobToBase64(imageBlob),
      contentId: birthdayBannerContentId,
      isInline: true,
    };
  }

  private async encodeImageAsDataUrl(imagePath: string): Promise<string> {
    return this.blobToDataUrl(await this.getImageBlob(imagePath));
  }

  private async getImageBlob(imagePath: string): Promise<Blob> {
    const response = await fetch(imagePath);

    if (!response.ok) {
      throw new Error(`Unable to load image from ${imagePath}`);
    }

    return response.blob();
  }

  private blobToBase64(imageBlob: Blob): Promise<string> {
    return this.blobToDataUrl(imageBlob).then((dataUrl) => {
      const base64Content = dataUrl.split(',')[1];

      if (base64Content) {
        return base64Content;
      }

      throw new Error('Unable to read base64 image content');
    });
  }

  private blobToDataUrl(imageBlob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
          return;
        }

        reject(new Error('Unable to encode image as base64'));
      };

      reader.onerror = () => {
        reject(reader.error ?? new Error('Unable to encode image as base64'));
      };

      reader.readAsDataURL(imageBlob);
    });
  }

  private buildEmailBody(emailMessage: EmailMessage, imageSource: string): string {
    const formattedMessage = this.escapeHtml(emailMessage.body).replace(
      /\r?\n/g,
      '<br />',
    );

    return [
      '<div>',
      `<img src="${imageSource}" alt="Birthday card" style="max-width: 100%; height: auto;" />`,
      `<p>${formattedMessage}</p>`,
      '</div>',
    ].join('');
  }

  private getFileNameFromPath(imagePath: string): string {
    const imageUrl = new URL(imagePath, window.location.origin);
    return imageUrl.pathname.split('/').pop() || 'birthday-card-banner.png';
  }

  private getContentTypeFromFileName(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'gif':
        return 'image/gif';
      case 'webp':
        return 'image/webp';
      case 'png':
      default:
        return 'image/png';
    }
  }

  private escapeHtml(value: string): string {
    const htmlEntities: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };

    return value.replace(/[&<>"']/g, (character) => htmlEntities[character]);
  }
}
